import { u8equal } from "./binary";
import { getOriginPrivateDirectory } from "native-file-system-adapter";

/**
 * Sanitizes a filename for safe use with Origin Private File System (OPFS)
 * 
 * Ensures cross-browser compatibility by removing characters that may cause issues in various browsers.
 * This helps prevent browser crashes and ensures consistent behavior across platforms.
 * 
 * @param filename The original filename or identifier to sanitize
 * @returns A safe filename for OPFS use
 */
export function sanitizeOPFSFilename(filename: string): string {
  // Don't process empty filenames
  if (!filename) return 'unnamed';
  
  // Replace reserved characters with underscores
  let sanitized = filename
    .replace(/[<>:"\/\\|?*]/g, '_')  // Replace < > : " / \ | ? *
    .replace(/[\x00-\x1F]/g, '_');    // Replace ASCII control characters (0-31)
  
  // Remove trailing spaces and periods
  sanitized = sanitized.replace(/[\s.]+$/g, '');
  
  // Handle edge case where we've sanitized everything away
  if (sanitized === '') return 'unnamed';
  
  // Handle reserved device names (case insensitive)
  const reservedNames = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i;
  if (reservedNames.test(sanitized) || reservedNames.test(sanitized.split('.')[0])) {
    sanitized = `safe_${sanitized}`;
  }
  
  return sanitized;
}

export async function getFileFromOPFS(path: string) {
  const root = await getOriginPrivateDirectory();
  const safePath = sanitizeOPFSFilename(path);

  try {
    const handle = await root.getFileHandle(safePath, {
      create: false,
    });

    // Use async version for potentially large files to prevent blocking
    const file = await getFileFromOPFSAsync(handle);

    return file;
  } catch (err) {
    if (err instanceof DOMException && err.name === "NotFoundError") {
        return null;
    }
    throw err;
  }
}

/**
 * Async OPFS file reading that yields to main thread for large files
 * This prevents the main thread blocking that causes ~200kbps bottleneck
 */
export async function getFileFromOPFSAsync(handle: any): Promise<File> {
  const file = await handle.getFile();
  
  // For large files (>100MB), yield to main thread to prevent blocking
  // This is especially important during file assembly operations
  if (file.size > 100 * 1024 * 1024) {
    console.log(`[OPFS_ASYNC] Reading large file (${file.size} bytes), yielding to main thread`);
    
    // Yield to main thread to prevent blocking during large file access
    await new Promise(resolve => setTimeout(resolve, 0));
    
    console.log(`[OPFS_ASYNC] Large file read completed`);
  }
  
  return file;
}

/**
 * Memory-efficient streaming download that avoids URL.createObjectURL duplication
 * 
 * This function triggers a download using OPFS file streaming instead of loading
 * the entire file into memory as a blob. This prevents memory doubling issues
 * for large files (e.g., 2GB file causing 4GB memory usage).
 * 
 * @param rootHash OPFS file identifier (hash)
 * @param filename Download filename
 * @param onProgress Optional progress callback for large file downloads
 * @returns Promise that resolves when download is triggered
 */
export async function triggerStreamingDownload(
  rootHash: string,
  filename: string,
  onProgress?: (progress: { bytesProcessed: number; totalBytes: number }) => void
): Promise<void> {
  console.log(`[STREAMING_DOWNLOAD] Initiating memory-efficient download for ${filename} (${rootHash.slice(0, 8)}...)`);
  
  try {
    const root = await getOriginPrivateDirectory();
    const safePath = sanitizeOPFSFilename(rootHash);
    
    // Get OPFS file handle
    const handle = await root.getFileHandle(safePath, { create: false });
    const opfsFile = await handle.getFile();
    
    console.log(`[STREAMING_DOWNLOAD] OPFS file found:`, {
      name: filename,
      size: opfsFile.size,
      type: opfsFile.type,
      rootHash: rootHash.slice(0, 8) + '...'
    });
    
    // For small files (<100MB), use traditional approach (minimal memory impact)
    if (opfsFile.size < 100 * 1024 * 1024) {
      console.log(`[STREAMING_DOWNLOAD] Using traditional download for small file (${opfsFile.size} bytes)`);
      const url = URL.createObjectURL(opfsFile);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      console.log(`[STREAMING_DOWNLOAD] Traditional download triggered for ${filename}`);
      return;
    }
    
    // For large files (>100MB), use chunked approach to avoid memory exhaustion
    console.log(`[STREAMING_DOWNLOAD] Using chunked streaming for large file (${opfsFile.size} bytes)`);
    
    // CRITICAL FIX: Don't use response.blob() for large files!
    // response.blob() loads entire file into memory → 13GB RAM usage → corruption
    // Instead: Read file in chunks, assemble Blob from chunks
    
    const CHUNK_SIZE = 100 * 1024 * 1024; // 100MB chunks for safety
    const chunks: BlobPart[] = [];
    let offset = 0;
    
    console.log(`[STREAMING_DOWNLOAD] Reading file in ${Math.ceil(opfsFile.size / CHUNK_SIZE)} chunks...`);
    
    while (offset < opfsFile.size) {
      const chunkEnd = Math.min(offset + CHUNK_SIZE, opfsFile.size);
      const chunk = opfsFile.slice(offset, chunkEnd);
      chunks.push(chunk);
      offset = chunkEnd;
      
      // Log progress for large files
      if (opfsFile.size > 1024 * 1024 * 1024) { // >1GB
        console.log(`[STREAMING_DOWNLOAD] Loaded chunk ${chunks.length}/${Math.ceil(opfsFile.size / CHUNK_SIZE)} (${(offset / opfsFile.size * 100).toFixed(1)}%)`);
      }
      
      // Yield to main thread every 5 chunks to prevent blocking
      if (chunks.length % 5 === 0) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }
    
    console.log(`[STREAMING_DOWNLOAD] Assembling ${chunks.length} chunks into final blob...`);
    const downloadBlob = new Blob(chunks, { type: 'application/octet-stream' });
    chunks.length = 0; // Free chunk references
    
    // Trigger download using assembled blob
    const url = URL.createObjectURL(downloadBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    
    console.log(`[STREAMING_DOWNLOAD] Chunked streaming download triggered for ${filename} (${downloadBlob.size} bytes)`);
    
    // Optional progress reporting for future enhancement
    if (onProgress) {
      onProgress({ bytesProcessed: opfsFile.size, totalBytes: opfsFile.size });
    }
    
  } catch (error) {
    console.error(`[STREAMING_DOWNLOAD_ERROR] Failed to trigger streaming download for ${filename}:`, error);
    throw new Error(`Streaming download failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

// PERFORMANCE FIX: Synced with provider.tsx CHUNK_SIZE_CONFIG
// Minimal OPFS chunk sizing config aligned with provider fallback logic
const OPFS_CHUNK_SIZE_CONFIG = {
  TINY: 256 * 1024,    // 256KB - EXTREME conditions only
  SMALL: 512 * 1024,   // 512KB - for poor connections
  STANDARD: 2 * 1024 * 1024, // 2MB - balanced performance (was 1MB)
  LARGE: 4 * 1024 * 1024,   // 4MB - for good connections
  XLARGE: 6 * 1024 * 1024, // 6MB - for very good connections
  XXLARGE: 8 * 1024 * 1024, // 8MB - for excellent connections
  XXXLARGE: 10 * 1024 * 1024, // 10MB - for excellent connections or large files

} as const;

// Bridge: allow provider to register a resolver for adaptive OPFS chunk size
type OPFSChunkSizeResolver = (fileSize: number) => number | null | undefined;
let _opfsChunkSizeResolver: OPFSChunkSizeResolver | null = null;

/**
 * Register or clear a resolver used by OPFS chunk sizing.
 * Pass null to clear.
 */
export function setOPFSChunkSizeResolver(resolver: OPFSChunkSizeResolver | null) {
  _opfsChunkSizeResolver = resolver;
}

/** Clear any previously registered OPFS chunk size resolver. */
export function clearOPFSChunkSizeResolver() {
  _opfsChunkSizeResolver = null;
}

/**
 * Get optimal OPFS chunk size for file operations
 * This delegates to the adaptive chunking system to ensure consistency
 * between network transfer chunks and OPFS I/O chunks, eliminating mismatches.
 */
export function getOptimalOPFSChunkSize(fileSize: number): number {
  // 1) Provider bridge via registered resolver (synchronous)
  // Allows the provider to inject its current adaptive chunk sizing decision
  // without introducing import cycles.
  try {
    if (_opfsChunkSizeResolver) {
      const size = _opfsChunkSizeResolver(fileSize);
      if (typeof size === 'number' && Number.isFinite(size) && size > 0) return size;
    }
  } catch (e) {
    console.warn('[OPFS_CHUNK_SIZE] Resolver error, falling back:', e);
  }

  // 2) Optional global hook (e.g., extension or host can attach)
  try {
    const anyGlobal = globalThis as any;
    const globalSizer = anyGlobal?.__MOBY_OPFS_CHUNK_SIZER__;
    if (typeof globalSizer === 'function') {
      const size = globalSizer(fileSize);
      if (typeof size === 'number' && Number.isFinite(size) && size > 0) return size;
    }
  } catch (e) {
    console.warn('[OPFS_CHUNK_SIZE] Global sizer error, falling back:', e);
  }

  // 3) Provider-aligned fallback heuristics (must mirror provider.tsx)
  // PERFORMANCE FIX: Updated to match new chunk size floor
  // Provider fallback: STANDARD (2MB) by default, larger for big files
  // See: getOptimalChunkSize() fallback logic in `src/components/provider.tsx`
  if (fileSize > 1024 * 1024 * 1024) {
    return OPFS_CHUNK_SIZE_CONFIG.XLARGE; // 6MB for files > 1GB
  }
  if (fileSize > 100 * 1024 * 1024) {
    return OPFS_CHUNK_SIZE_CONFIG.LARGE; // 4MB for files > 100MB
  }
  return OPFS_CHUNK_SIZE_CONFIG.STANDARD; // 2MB default
}

export async function saveFileToOPFS(
  target: File,
  path: string,
  checksum?: Uint8Array,
) {
  const root = await getOriginPrivateDirectory();
  const safePath = sanitizeOPFSFilename(path);

  const handle = await root.getFileHandle(safePath, {
    create: true,
  });

  const stored = await handle.getFile();
  if (checksum !== undefined && u8equal(await hashBlob(stored), checksum)) {
    return;
  }

  // Use async chunked writing to prevent main thread blocking
  await saveFileToOPFSAsync(handle, target);
}

/**
 * Async OPFS file saving with chunked writing to prevent main thread blocking
 * This fixes the ~200kbps throughput bottleneck by yielding to the main thread
 * between chunks, allowing other operations to continue.
 */
export async function saveFileToOPFSAsync(
  handle: any, // Use any to handle native-file-system-adapter compatibility
  file: File,
  chunkSize?: number, // Optional adaptive chunk size - will use optimal size if not provided
  onProgress?: (progress: number) => void // Optional progress callback (0-100)
): Promise<void> {
  // Use adaptive chunk size if not provided
  const effectiveChunkSize = chunkSize || getOptimalOPFSChunkSize(file.size);
  const stream = await handle.createWritable();
  
  let writeSucceeded = false;
  let writeError: Error | null = null;
  let bytesWritten = 0; // Track progress for error logging
  
  try {
    // For small files (<10MB), use direct write to avoid chunking overhead
    if (file.size < 10 * 1024 * 1024) {
      if (onProgress) {
        onProgress(0); // Start progress
      }
      await stream.write(file);
      if (onProgress) {
        onProgress(100); // Complete progress
      }
      writeSucceeded = true;
      return;
    }

    // For large files, use chunked writing with main thread yielding
    console.log(`[OPFS_ASYNC] Starting chunked write for ${file.size} byte file with ${effectiveChunkSize} byte chunks (adaptive: ${!chunkSize})`);
    
    const totalBytes = file.size;
    
    // Process file in chunks to prevent main thread blocking
    while (bytesWritten < totalBytes) {
      const remainingBytes = totalBytes - bytesWritten;
      const currentChunkSize = Math.min(effectiveChunkSize, remainingBytes);
      
      // Read chunk from file
      const chunk = file.slice(bytesWritten, bytesWritten + currentChunkSize);
      
      // Write chunk to OPFS
      await stream.write(chunk);
      
      bytesWritten += currentChunkSize;
      
      // Yield to main thread every chunk to prevent blocking
      // This is critical for maintaining UI responsiveness during large transfers
      // Bug 6 fix: Use a longer yield every 20 chunks (HEARTBEAT_YIELD_MS) to ensure
      // WebRTC ICE keepalives and DTLS heartbeats can fire during multi-GB writes.
      if (bytesWritten < totalBytes) {
        const chunkCount = Math.round(bytesWritten / effectiveChunkSize);
        const yieldMs = (chunkCount % 20 === 0) ? 50 : 0;
        await new Promise(resolve => setTimeout(resolve, yieldMs));
      }
      
      // Progress reporting for UI updates
      const progressPercent = (bytesWritten / totalBytes) * 100;
      
      // Call progress callback if provided
      if (onProgress) {
        onProgress(progressPercent);
      }
      
      // Optional: Log progress for large files (every 10MB)
      if (bytesWritten % (10 * 1024 * 1024) === 0 || bytesWritten === totalBytes) {
        console.log(`[OPFS_ASYNC] Write progress: ${progressPercent.toFixed(1)}% (${bytesWritten}/${totalBytes} bytes)`);
      }
    }
    
    console.log(`[OPFS_ASYNC] Completed chunked write for ${totalBytes} bytes`);
    writeSucceeded = true;
  } catch (error) {
    // FIX: Capture write error for proper cleanup
    writeError = error as Error;
    console.error(`[OPFS_ERROR] Write failed:`, {
      error: error instanceof Error ? error.message : String(error),
      bytesWritten: file.size < 10 * 1024 * 1024 ? 0 : bytesWritten,
      totalBytes: file.size,
      // Common causes: quota exceeded (incognito mode), disk full, OPFS corruption
      likelyCause: error instanceof DOMException && error.name === 'QuotaExceededError' 
        ? 'OPFS quota exceeded (common in incognito mode)' 
        : 'Unknown - check browser console'
    });
    throw error;
  } finally {
    // CRITICAL FIX: Use abort() for errored stream, close() for successful stream
    // Calling close() on an errored stream throws "Cannot close a ERRORED writable stream"
    try {
      if (writeSucceeded) {
        await stream.close();
      } else {
        // Stream errored during write - abort instead of close
        await stream.abort();
        console.log(`[OPFS_CLEANUP] Stream aborted due to write error`);
      }
    } catch (cleanupError) {
      // Suppress cleanup errors - they're secondary to the write error
      console.warn(`[OPFS_CLEANUP] Cleanup error (non-critical):`, cleanupError);
    }
  }
}

/**
 * Read only a specific range from an OPFS file without loading entire file
 * This is critical for memory efficiency with large files
 */
export async function getFileRangeFromOPFS(
  path: string, 
  startOffset: number, 
  endOffset: number
): Promise<Uint8Array | null> {
  const root = await getOriginPrivateDirectory();
  const safePath = sanitizeOPFSFilename(path);

  try {
    const handle = await root.getFileHandle(safePath, { create: false });
    const file = await handle.getFile();
    
    // Use slice to get only the range we need
    const slice = file.slice(startOffset, endOffset);
    
    // Convert to ArrayBuffer then Uint8Array
    const buffer = await slice.arrayBuffer();
    
    console.log(`[OPFS_RANGE] Read range ${startOffset}-${endOffset} from ${safePath} (${buffer.byteLength} bytes)`);
    
    return new Uint8Array(buffer);
  } catch (err) {
    if (err instanceof DOMException && err.name === "NotFoundError") {
      return null;
    }
    // P0-Q: Treat NotReadableError the same as NotFoundError — return null so
    // the caller (getFileRange in provider.tsx) falls through to the
    // senderFiles in-memory File fallback. Brave/Chromium transiently
    // throws NotReadableError on slice().arrayBuffer() under storage
    // pressure during multi-hour / multi-GB transfers; rethrowing would
    // surface as an uncaught promise rejection in the FILE_CHUNK_REQUEST
    // handler and kill the whole transfer. Logging at warn (not error)
    // because the fallback path makes this recoverable.
    if (err instanceof DOMException && err.name === "NotReadableError") {
      console.warn(`[OPFS_RANGE] NotReadableError reading ${safePath} [${startOffset}-${endOffset}] — falling through to senderFiles fallback`);
      return null;
    }
    throw err;
  }
}

export async function* chunkStream(
  stream: ReadableStream<Uint8Array>,
  chunkSize: number,
) {
  const reader = stream.getReader();

  try {
    // Pre-allocate buffer to avoid repeated allocations
    // Use 2x chunk size to handle boundary cases efficiently
    const maxBufferSize = chunkSize * 2;
    let buffer = new Uint8Array(maxBufferSize);
    let bufferLength = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      // Handle buffer overflow by yielding complete chunks first
      if (bufferLength + value.byteLength > maxBufferSize) {
        // Yield complete chunks from current buffer
        while (bufferLength >= chunkSize) {
          yield buffer.slice(0, chunkSize);
          // Shift remaining data to start of buffer
          const remaining = bufferLength - chunkSize;
          if (remaining > 0) {
            buffer.copyWithin(0, chunkSize, bufferLength);
          }
          bufferLength = remaining;
        }
      }

      // Add new data to buffer
      buffer.set(value, bufferLength);
      bufferLength += value.byteLength;

      // Yield complete chunks
      while (bufferLength >= chunkSize) {
        yield buffer.slice(0, chunkSize);
        // Shift remaining data to start of buffer  
        const remaining = bufferLength - chunkSize;
        if (remaining > 0) {
          buffer.copyWithin(0, chunkSize, bufferLength);
        }
        bufferLength = remaining;
      }
    }

    // Yield final partial chunk if any data remains
    if (bufferLength > 0) {
      yield buffer.slice(0, bufferLength);
    }
  } finally {
    reader.releaseLock();
  }
}

export async function hash(chunk: BufferSource) {
  return new Uint8Array(await crypto.subtle.digest("SHA-256", chunk));
}

export async function hashBlob(blob: Blob) {
  // Use streaming for large blobs to avoid memory bloat
  if (blob.size > 100 * 1024 * 1024) { // > 100MB
    const stream = blob.stream();
    const reader = stream.getReader();
    const chunks: Uint8Array[] = [];
    
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
      }
    } finally {
      reader.releaseLock();
    }
    
    // Combine chunks and hash
    const totalSize = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
    const combined = new Uint8Array(totalSize);
    let offset = 0;
    for (const chunk of chunks) {
      combined.set(chunk, offset);
      offset += chunk.byteLength;
    }
    return hash(combined);
  }
  
  // For smaller blobs, use direct approach
  return hash(await blob.arrayBuffer());
}

export async function constructMerkleTree(
  file: File, 
  chunkSize: number,
  onProgress?: (progress: number) => void // Optional progress callback (0-100)
) {
  // Process chunks in batches to avoid memory exhaustion on large files
  const BATCH_SIZE = 50; // Process 50 chunks at a time
  const totalChunks = Math.ceil(file.size / chunkSize);
  
  // For massive files (>10GB), use disk-based approach
  const USE_DISK_STORAGE = file.size > 10 * 1024 * 1024 * 1024;
  
  if (USE_DISK_STORAGE) {
    console.log(`[MERKLE_DISK] Using disk-based Merkle generation for ${(file.size / (1024**3)).toFixed(1)}GB file`);
    return await constructMerkleTreeWithDiskStorage(file, chunkSize, onProgress);
  }
  
  // Original in-memory approach for smaller files
  const leaves: Uint8Array[] = [];
  
  console.log(`[MERKLE_BATCHED] Processing ${totalChunks} chunks in batches of ${BATCH_SIZE}`);
  
  // Process chunks in batches
  for (let i = 0; i < file.size; i += chunkSize * BATCH_SIZE) {
    const batchPromises = [];
    const batchEnd = Math.min(i + chunkSize * BATCH_SIZE, file.size);
    
    // Create promises for this batch only
    for (let j = i; j < batchEnd; j += chunkSize) {
      batchPromises.push(hashBlob(file.slice(j, j + chunkSize)));
    }
    
    // Process batch and add to leaves
    const batchResults = await Promise.all(batchPromises);
    leaves.push(...batchResults);
    
    // COOPERATIVE YIELDING: Every 100 chunks, pause to let browser breathe
    // This prevents "setInterval handler took too long" violations and keeps UI responsive
    // during CPU-intensive merkle tree generation (5-6 minutes for 6GB files)
    const chunksProcessed = leaves.length;
    if (chunksProcessed > 0 && chunksProcessed % 100 === 0) {
      // setTimeout(fn, 0) yields to event loop for 1 tick
      // Allows: UI updates, user interactions, timers, network events
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    
    // Report progress for UI updates (batched - minimal overhead)
    const processed = leaves.length;
    const percent = Math.round((processed / totalChunks) * 100);
    
    // Call progress callback if provided
    if (onProgress) {
      onProgress(percent);
    }
    
    // Log progress for large files
    if (totalChunks > 100) {
      console.log(`[MERKLE_PROGRESS] Hashed ${processed}/${totalChunks} chunks (${percent}%)`);
    }
  }

  const tree: Uint8Array[][] = [leaves];

  while (tree[tree.length - 1].length > 1) {
    const currentLevel = tree[tree.length - 1];
    const nextLevel: Uint8Array[] = [];
    for (let i = 0; i < currentLevel.length; i += 2) {
      const left = currentLevel[i];
      const right = i + 1 < currentLevel.length ? currentLevel[i + 1] : left;
      const combined = new Uint8Array(left.length + right.length);
      combined.set(left);
      combined.set(right, left.length);
      nextLevel.push(await hash(combined));
    }
    tree.push(nextLevel);
  }

  return tree;
}

async function constructMerkleTreeWithDiskStorage(
  file: File, 
  chunkSize: number,
  onProgress?: (progress: number) => void
) {
  const totalChunks = Math.ceil(file.size / chunkSize);
  const BATCH_SIZE = 100; // Larger batch for disk-based
  const tempId = `merkle-temp-${Date.now()}`;
  
  try {
    // Get OPFS root
    const opfsRoot = await navigator.storage.getDirectory();
    const tempDir = await opfsRoot.getDirectoryHandle('merkle-temp', { create: true });
    
    // Phase 1: Generate and store leaf hashes
    console.log(`[MERKLE_DISK] Phase 1: Generating ${totalChunks} leaf hashes`);
    const leafFile = await tempDir.getFileHandle(`${tempId}-level-0`, { create: true });
    const leafWriter = await leafFile.createWritable();
    
    for (let i = 0; i < file.size; i += chunkSize * BATCH_SIZE) {
      const batchPromises = [];
      const batchEnd = Math.min(i + chunkSize * BATCH_SIZE, file.size);
      
      for (let j = i; j < batchEnd; j += chunkSize) {
        batchPromises.push(hashBlob(file.slice(j, j + chunkSize)));
      }
      
      const batchHashes = await Promise.all(batchPromises);
      
      // Write batch to disk
      for (const hash of batchHashes) {
        await leafWriter.write(hash);
      }
      
      // Log progress
      const processed = Math.min(Math.floor((i + chunkSize * BATCH_SIZE) / chunkSize), totalChunks);
      const percent = Math.round((processed / totalChunks) * 100);
      console.log(`[MERKLE_DISK] Leaf generation: ${processed}/${totalChunks} (${percent}%)`);
      
      // Call progress callback if provided
      if (onProgress) {
        onProgress(percent);
      }
      
      // Yield to prevent blocking
      if (percent % 10 === 0) {
        await new Promise(r => setTimeout(r, 0));
      }
    }
    
    await leafWriter.close();
    
    // Phase 2: Build tree levels
    console.log(`[MERKLE_DISK] Phase 2: Building tree levels`);
    const tree: Uint8Array[][] = [];
    let currentLevelSize = totalChunks;
    let levelIndex = 0;
    
    while (currentLevelSize > 1) {
      const currentFile = await tempDir.getFileHandle(`${tempId}-level-${levelIndex}`, { create: false });
      const currentData = await (await currentFile.getFile()).arrayBuffer();
      const currentHashes = new Uint8Array(currentData);
      
      // Store this level in memory (only current level)
      const level: Uint8Array[] = [];
      for (let i = 0; i < currentHashes.length; i += 32) {
        level.push(currentHashes.slice(i, i + 32));
      }
      tree.push(level);
      
      // Generate next level
      const nextLevelSize = Math.ceil(currentLevelSize / 2);
      const nextFile = await tempDir.getFileHandle(`${tempId}-level-${levelIndex + 1}`, { create: true });
      const nextWriter = await nextFile.createWritable();
      
      for (let i = 0; i < level.length; i += 2) {
        const left = level[i];
        const right = i + 1 < level.length ? level[i + 1] : left;
        const combined = new Uint8Array(64);
        combined.set(left);
        combined.set(right, 32);
        const parentHash = await hash(combined);
        await nextWriter.write(parentHash);
      }
      
      await nextWriter.close();
      
      // Clear the current level from memory (keep only what we need)
      if (levelIndex > 0) {
        tree[levelIndex - 1] = []; // Clear previous level
      }
      
      currentLevelSize = nextLevelSize;
      levelIndex++;
      console.log(`[MERKLE_DISK] Built level ${levelIndex} with ${nextLevelSize} nodes`);
    }
    
    // Read final root level
    const rootFile = await tempDir.getFileHandle(`${tempId}-level-${levelIndex}`, { create: false });
    const rootData = await (await rootFile.getFile()).arrayBuffer();
    const rootHash = new Uint8Array(rootData);
    tree.push([rootHash]);
    
    // Reconstruct full tree by reading all levels back
    const fullTree: Uint8Array[][] = [];
    for (let i = 0; i <= levelIndex; i++) {
      const levelFile = await tempDir.getFileHandle(`${tempId}-level-${i}`, { create: false });
      const levelData = await (await levelFile.getFile()).arrayBuffer();
      const levelHashes = new Uint8Array(levelData);
      
      const level: Uint8Array[] = [];
      for (let j = 0; j < levelHashes.length; j += 32) {
        level.push(levelHashes.slice(j, j + 32));
      }
      fullTree.push(level);
    }
    
    // Cleanup temp files
    for (let i = 0; i <= levelIndex; i++) {
      try {
        await tempDir.removeEntry(`${tempId}-level-${i}`);
      } catch {}
    }
    
    console.log(`[MERKLE_DISK] Tree generation complete: ${fullTree.length} levels`);
    return fullTree;
    
  } catch (error) {
    console.error(`[MERKLE_DISK] Failed to generate tree with disk storage:`, error);
    // Fallback to memory approach if disk fails
    throw error;
  }
}

export async function generateMerkleProof(
  tree: Uint8Array[][],
  chunkIndex: number,
) {
  const proof: Uint8Array[] = [];

  let index = chunkIndex;

  for (let level = 0; level < tree.length - 1; level++) {
    const currentLevel = tree[level];
    let siblingIndex: number;
    if (index % 2 === 0) {
      siblingIndex = index + 1;
    } else {
      siblingIndex = index - 1;
    }

    if (siblingIndex >= currentLevel.length) {
      siblingIndex = index;
    }
    proof.push(currentLevel[siblingIndex]);
    index = Math.floor(index / 2);
  }

  const root = tree[tree.length - 1][0];

  return { proof, root };
}

export async function validateMerkleProof(
  proof: Uint8Array[],
  chunkHash: Uint8Array,
  chunkIndex: number,
) {
  let currentHash = chunkHash;
  let currentIndex = chunkIndex;

  for (const sibling of proof) {
    let combined: Uint8Array;
    if (currentIndex % 2 === 0) {
      combined = new Uint8Array(currentHash.length + sibling.length);
      combined.set(currentHash);
      combined.set(sibling, currentHash.length);
    } else {
      combined = new Uint8Array(sibling.length + currentHash.length);
      combined.set(sibling);
      combined.set(currentHash, sibling.length);
    }

    // TypeScript fix: Use type assertion since Uint8Array constructor always uses ArrayBuffer (not SharedArrayBuffer)
    currentHash = await hash(combined as BufferSource);
    currentIndex = Math.floor(currentIndex / 2);
  }

  return currentHash;
}

// ─── OPFS Health Probe ───────────────────────────────────────────────────────
// Lightweight write/read/delete cycle to detect broken OPFS state at boot.
// Caches result so subsequent calls are free. Call resetOPFSHealthCheck() to
// re-probe (e.g. after a page-visibility change that may clear stale handles).

let _opfsHealthy: boolean | null = null;
let _opfsProbeInFlight: Promise<boolean> | null = null;

/**
 * Returns true if OPFS is functional.  First call runs a real probe;
 * subsequent calls return the cached result.  Safe to call concurrently —
 * only one probe runs at a time.
 */
export function isOPFSHealthy(): Promise<boolean> {
  if (_opfsHealthy !== null) return Promise.resolve(_opfsHealthy);
  if (_opfsProbeInFlight) return _opfsProbeInFlight;

  _opfsProbeInFlight = (async () => {
    if (!navigator.storage?.getDirectory) {
      _opfsHealthy = false;
      return false;
    }

    // OPFS_FIX: Retry probe once on failure. Under memory pressure (e.g. during
    // AV streams), the first attempt may hit an InvalidStateError due to stale
    // cached state. A second attempt with fresh handles often succeeds.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const root = await navigator.storage.getDirectory();
        const name = '__opfs_health_probe__';
        const handle = await root.getFileHandle(name, { create: true });
        const writable = await handle.createWritable();
        const payload = new Uint8Array([0x4D, 0x4F, 0x42, 0x59]); // "MOBY"
        await writable.write(payload);
        await writable.close();

        const file = await handle.getFile();
        const readback = new Uint8Array(await file.arrayBuffer());

        // Clean up probe file
        try { await root.removeEntry(name); } catch { /* best-effort */ }

        _opfsHealthy = readback.length === 4 &&
          readback[0] === 0x4D && readback[1] === 0x4F &&
          readback[2] === 0x42 && readback[3] === 0x59;

        console.log(`[OPFS_HEALTH] Probe result: ${_opfsHealthy ? 'healthy' : 'CORRUPT (readback mismatch)'}${attempt > 0 ? ' (retry succeeded)' : ''}`);
        return _opfsHealthy;
      } catch (err) {
        if (attempt === 0) {
          console.warn(`[OPFS_HEALTH] Probe attempt ${attempt + 1} failed, retrying with fresh handles...`, err);
          // Brief pause before retry to let any pending I/O settle
          await new Promise(r => setTimeout(r, 200));
          continue;
        }
        _opfsHealthy = false;
        console.warn('[OPFS_HEALTH] Probe FAILED on both attempts — OPFS is unavailable for this session', err);
        return false;
      }
    }

    _opfsHealthy = false;
    return false;
  })();

  // OPFS_FIX: Clear the in-flight promise when done (was already doing this
  // in the finally block but moved here for clarity with the retry loop)
  _opfsProbeInFlight.finally(() => { _opfsProbeInFlight = null; });

  return _opfsProbeInFlight;
}

/** Re-probe on next call (e.g. after visibility change). */
export function resetOPFSHealthCheck(): void {
  _opfsHealthy = null;
}

/** In-memory size threshold: files below this can skip OPFS entirely. */
export const OPFS_FALLBACK_MEMORY_LIMIT = 50 * 1024 * 1024; // 50 MB
